import type { i18n as I18nInstance, TFunction } from "i18next";
import type { ReplayFailure } from "@/lib/mutationRegistry";
import { extractApiError } from "@/utils/apiErrorMapper";
import { translateBackendError } from "@/utils/errorTranslation";

/**
 * Single source of truth for "what does this failure say to the user".
 *
 * Total over every `ReplayFailure` variant — both write paths normalize into
 * one before calling it: the online toast path (`mutationToasts`) from a live
 * `AxiosError`, the offline queue sheet from the persisted snapshot. There is
 * no variant that reaches the user as raw English.
 *
 * The `server-error` arm mirrors the online chain rung-for-rung:
 *   1. `errors.api.<code>` when the backend returned a recognized code.
 *   2. `translateBackendError(message)` pattern-match (parity with the toast).
 *   3. `fallbackMessage` — per-mutation `meta.toastMessages.error`, supplied
 *      only by the online caller (a persisted queue item has no live meta).
 *   4. `errors.api.INTERNAL_ERROR` floor.
 */
export function resolveReplayErrorMessage(
	outcome: ReplayFailure,
	t: TFunction,
	i18n: I18nInstance,
	fallbackMessage?: string,
): string {
	switch (outcome.type) {
		case "server-error": {
			const { code, message } = outcome.apiError;
			const codeKey = code ? `errors.api.${code}` : undefined;
			if (codeKey && i18n.exists(codeKey)) return t(codeKey);
			// translateBackendError echoes its input when no pattern matches, so it
			// only yields a message when there is one; otherwise fall through.
			if (message) return translateBackendError(message, t);
			return fallbackMessage ?? t("errors.api.INTERNAL_ERROR");
		}
		case "network-error":
			return t("errors.offlineReplay.networkError");
		case "version-mismatch":
			return t("errors.offlineReplay.versionMismatch");
		case "unknown-key":
			return t("errors.offlineReplay.unknownKey");
	}
}

/**
 * Resolves a mutation rejection into a user-facing string for the in-form
 * `root.server` slot (§3.5b). Mirrors the `server-error` chain of
 * `resolveReplayErrorMessage`, but its floor is a **localized** generic
 * ("forms.validation.serverError") rather than the raw backend message — the
 * envelope's `message` carries a developer-facing English string (e.g. "Please
 * check the backend server logs for more information"), which must never reach
 * a pt-BR user. Known `code`s and recognized message patterns still translate.
 */
export function resolveFormServerError(
	error: unknown,
	t: TFunction,
	i18n: I18nInstance,
): string {
	const apiError = extractApiError(error);
	const codeKey = apiError?.code ? `errors.api.${apiError.code}` : undefined;
	if (codeKey && i18n.exists(codeKey)) return t(codeKey);

	// translateBackendError echoes its input when no pattern matches — only trust
	// it when it actually translated, otherwise the raw dev string would leak.
	const message = apiError?.message;
	if (message) {
		const translated = translateBackendError(message, t);
		if (translated !== message) return translated;
	}

	return t("forms.validation.serverError");
}
